import { RouteObject } from 'react-router';
import { Navigate, useRoutes, useParams } from 'react-router-dom';

import InappChat from './Agency/Chat/Pubnub';
import PayInvoice from './Agency/PayInvoice';
import AppLogin from './AppLogin';
import LinkExpirePage from './LinkExpire';
import MobileIntro from './MobileIntro';
import PDFGeneration from './PDFGeneration';
import DataDeletionRequest from './DataDeletionRequest';
import QRDownload from './QRDownload';
import RequestAppAccess from './RequestAppAccess';
import ValidationAccount from './ValidationAccount';
import Waitlist from './Waitlist';

import Config from '@/config';
import Agency from '@/routes/Agency';
import AgencyCustomerAccountDetail from '@/routes/Agency/Account';
import BackgroundAudit from '@/routes/Agency/BackgroundAudit';
import Compliance from '@/routes/Agency/Compliance';
import AgencyChat from '@/routes/Agency/Chat/Chat';
import AgencyContacts from '@/routes/Agency/Contacts';
import AgencyCreateGigOrder from '@/routes/Agency/CreateGigOrder';
import AgencyCustomerDetail from '@/routes/Agency/Customer';
import AgencyCustomerAdminDetail from '@/routes/Agency/CustomerAdmin';
import AgencyCustomers from '@/routes/Agency/Customers';
import AgencyDashboard from '@/routes/Agency/Dashboard';
import InvoiceGenerator from '@/routes/Agency/InvoiceGenerator';
import InvoiceHealth from '@/routes/Agency/InvoiceHealth';
import Invoices from '@/routes/Agency/Invoices';
import InvoiceDetail from '@/routes/Agency/Invoices/InvoiceDetailsRevamp';
import InvoicePay from '@/routes/Agency/Invoices/InvoicePay';
import AgencyOrderJobDetail from '@/routes/Agency/Job';
import JobMonitor from '@/routes/Agency/JobMonitor';
import AgencyOrderJobDetailRevamp from '@/routes/Agency/JobOld';
import MyTeam from '@/routes/Agency/MyTeam';
import AgencyOrderDetail from '@/routes/Agency/Order';
import AgencyOrders from '@/routes/Agency/Orders';
import AgencyPayrolls from '@/routes/Agency/Payrolls';
import AgencyPayrollDetail from '@/routes/Agency/Payrolls/PayrollDetail';
import AgencyPayrollsV2 from '@/routes/Agency/PayrollsV2';
import AgencyPayrollDetailV2 from '@/routes/Agency/PayrollsV2/PayrollDetailV2';
import AgencyReports from '@/routes/Agency/Reports';
import RestrictedAccess from '@/routes/Agency/RestrictedAccess';
import AgencyWorkers from '@/routes/Agency/Workers';
import AgencyWorkersV2 from '@/routes/Agency/WorkersV2';
import AgencyWorkerV2Detail from '@/routes/Agency/WorkerV2';
import SkillDetailPage from '@/routes/Agency/WorkerV2/SkillDetailPage';
import Authenticated from '@/routes/Authenticated';
import ForgotPassword from '@/routes/ForgotPassword';
import Login from '@/routes/Login';
import PrivateRoute from '@/routes/PrivateRoute';
import ResetPassword from '@/routes/ResetPassword';
import Root from '@/routes/Root';
import SignUp from '@/routes/SignUp';
import Signout from '@/routes/Signout';
import ROUTES from '@/routes/routes';
import { UserRoleEnum } from '@/types/graphql';
import NotificationHub from './Agency/NotificationHub';
import PayrollAudit from './Agency/PayrollAudit';
import WorkbrightAudit from './Agency/WorkBrightAudit';
import ZealAudit from './Agency/ZealAudit';
import HelloSignAudit from './Agency/HelloSignAudit';
import TimesheetHealthChecks from './Agency/TimesheetHealthChecks';
import WorkerExclusionAnalyzer from './Agency/WorkerExclusionAnalyzer';
import OnboardingBypass from './Agency/OnboardingBypass';
import I9Bypass from './Agency/I9Bypass';
import PasswordReset from './Agency/PasswordReset';
import OrderInvites from './Agency/OrderInvites';
import JobDebugger from './Agency/JobDebugger';
import DocumentSigning from './DocumentSigning';
import JobCreator from './JobCreator';
import Posts from './Agency/Posts';
import PostForm from './Agency/Posts/PostForm';
import DocumentsList from './Agency/Documents';
import DocumentForm from './Agency/Documents/DocumentForm';
import DocumentShow from './Agency/Documents/DocumentShow';
import TestSignView from './Agency/Documents/TestSignView';
import AiAssessments from './Agency/AiAssessments';
import PendingUniforms from './TenantLayout/SkillManagement/Uniforms';
import PendingCertificates from './TenantLayout/SkillManagement/Certificates';
import NoshowAlerts from './Agency/NoshowAlerts';
import Skills from './Agency/Skills';
import Regions from './Agency/Regions';
import TasksRoutes from './Tasks';
import AppVersionPins from './Agency/AppVersionPins';
import AppBinaryVersionPins from './Agency/AppBinaryVersionPins';
import ClientSessionsPage from './Agency/ClientSessions';

const WorkerDetailRedirect = () => {
  const { workerId } = useParams();
  return <Navigate to={`/workers_v2/${workerId}`} replace />;
};

const privateRoutes: RouteObject[] = [
  {
    path: '*',
    element: <Navigate to={ROUTES.basePath} />,
  },
  {
    path: ROUTES.basePath, // "/"
    element: (
      <PrivateRoute
        element={<AgencyDashboard />}
        redirectTo={ROUTES.orders}
        requiredRoles={[
          UserRoleEnum.TENANT_ADMIN,
          UserRoleEnum.HELPDESK_ADMIN,
          UserRoleEnum.CUSTOMER_ADMIN,
        ]}
      />
    ),
  },
  {
    path: ROUTES.inappchat,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<InappChat />}
            redirectTo="/restricted-access"
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.orders,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyOrders />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.gigOrderCreate,
        element: (
          <PrivateRoute
            element={<AgencyCreateGigOrder />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.orderDetail,
        element: (
          <PrivateRoute
            element={<AgencyOrderDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.orderJobDetail,
        element: (
          <PrivateRoute
            element={<AgencyOrderJobDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.orderJobDetailOld,
        element: (
          <PrivateRoute
            element={<AgencyOrderJobDetailRevamp />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.clients,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyCustomers />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerAccountDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAccountDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.customerAdminDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAdminDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.contacts,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyContacts />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.invoices,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<Invoices />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.invoicesDetail,
        element: (
          <PrivateRoute
            element={<InvoiceDetail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.invoicesPay,
        element: (
          <PrivateRoute
            element={<InvoicePay />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.workers,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyWorkers />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerDetail,
        element: <WorkerDetailRedirect />,
      },
    ],
  },
  {
    path: ROUTES.workersV2,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyWorkersV2 />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerV2Detail,
        element: (
          <PrivateRoute
            element={<AgencyWorkerV2Detail />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
      {
        path: ROUTES.workerV2SkillDetail,
        element: (
          <PrivateRoute
            element={<SkillDetailPage />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myTeam,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<MyTeam />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.myTeamAdminDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAdminDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myCompany,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyCustomerDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.myCompanyAccountDetail,
        element: (
          <PrivateRoute
            element={<AgencyCustomerAccountDetail />}
            requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.chat,
    children: [
      {
        path: ROUTES.basePath, // "/"
        element: (
          <PrivateRoute
            element={<AgencyChat />}
            requiredRoles={[
              UserRoleEnum.TENANT_ADMIN,
              UserRoleEnum.HELPDESK_ADMIN,
              UserRoleEnum.CUSTOMER_ADMIN,
            ]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.myProfile,
    element: (
      <PrivateRoute
        element={<AgencyCustomerAdminDetail />}
        requiredRoles={[UserRoleEnum.CUSTOMER_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.payrolls,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyPayrolls />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.payrollDetail,
        element: (
          <PrivateRoute
            element={<AgencyPayrollDetail />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.payrollsV2,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyPayrollsV2 />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.payrollDetailV2,
        element: (
          <PrivateRoute
            element={<AgencyPayrollDetailV2 />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.payrollAudit,
    element: (
      <PrivateRoute
        element={<PayrollAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.backgroundAudit,
    element: (
      <PrivateRoute
        element={<BackgroundAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.orderInvites,
    element: (
      <PrivateRoute
        element={<OrderInvites />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.compliance,
    element: (
      <PrivateRoute
        element={<Compliance />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.workbrightAudit,
    element: (
      <PrivateRoute
        element={<WorkbrightAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.zealAudit,
    element: (
      <PrivateRoute
        element={<ZealAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.hellosignAudit,
    element: (
      <PrivateRoute
        element={<HelloSignAudit />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.timesheetHealthChecks,
    element: (
      <PrivateRoute
        element={<TimesheetHealthChecks />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.jobDebugger,
    element: (
      <PrivateRoute
        element={<JobDebugger />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.CUSTOMER_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.workerExclusionAnalyzer,
    element: (
      <PrivateRoute
        element={<WorkerExclusionAnalyzer />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.invoiceHealth,
    element: (
      <PrivateRoute
        element={<InvoiceHealth />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.reports,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<AgencyReports />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.jobmonitor,
    element: (
      <PrivateRoute
        element={<JobMonitor />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN, UserRoleEnum.HELPDESK_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.invoiceGenerator,
    element: (
      <PrivateRoute
        element={<InvoiceGenerator />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.notificationHub,
    element: (
      <PrivateRoute
        element={<NotificationHub />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.onboardingBypass,
    element: (
      <PrivateRoute
        element={<OnboardingBypass />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.i9Bypass,
    element: (
      <PrivateRoute
        element={<I9Bypass />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.passwordReset,
    element: (
      <PrivateRoute
        element={<PasswordReset />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.posts,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<Posts />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.postCreate,
        element: (
          <PrivateRoute
            element={<PostForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.postEdit,
        element: (
          <PrivateRoute
            element={<PostForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.documents,
    children: [
      {
        path: ROUTES.basePath,
        element: (
          <PrivateRoute
            element={<DocumentsList />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentCreate,
        element: (
          <PrivateRoute
            element={<DocumentForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentShow,
        element: (
          <PrivateRoute
            element={<DocumentShow />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentEdit,
        element: (
          <PrivateRoute
            element={<DocumentForm />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.documentTestSign,
        element: (
          <PrivateRoute
            element={<TestSignView />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
  {
    path: ROUTES.aiAssessments,
    element: (
      <PrivateRoute
        element={<AiAssessments />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.skills,
    element: (
      <PrivateRoute
        element={<Skills />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.regions,
    element: (
      <PrivateRoute
        element={<Regions />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.noshowAlerts,
    element: (
      <PrivateRoute
        element={<NoshowAlerts />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.appVersionPins,
    element: (
      <PrivateRoute
        element={<AppVersionPins />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.appBinaryVersionPins,
    element: (
      <PrivateRoute
        element={<AppBinaryVersionPins />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: `${ROUTES.tasks}/*`,
    element: (
      <PrivateRoute
        element={<TasksRoutes />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    // Hidden fleet view of worker client sessions. No sidebar entry — reachable
    // only by typing the URL directly.
    path: ROUTES.clientSessions,
    element: (
      <PrivateRoute
        element={<ClientSessionsPage />}
        requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
      />
    ),
  },
  {
    path: ROUTES.restrictedAccess,
    element: (
      <PrivateRoute
        element={<RestrictedAccess />}
        requiredRoles={[
          UserRoleEnum.TENANT_ADMIN,
          UserRoleEnum.HELPDESK_ADMIN,
          UserRoleEnum.CUSTOMER_ADMIN,
        ]}
      />
    ),
  },
  {
    path: ROUTES.skillManagement,
    children: [
      {
        path: ROUTES.pendingUniforms,
        element: (
          <PrivateRoute
            element={<PendingUniforms />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
      {
        path: ROUTES.pendingCertificates,
        element: (
          <PrivateRoute
            element={<PendingCertificates />}
            requiredRoles={[UserRoleEnum.TENANT_ADMIN]}
          />
        ),
      },
    ],
  },
];

const publicRoutes: RouteObject[] = [
  {
    path: ROUTES.linkExpire,
    element: <LinkExpirePage />,
  },
  {
    path: ROUTES.payInvoice,
    element: <PayInvoice />,
  },
  {
    path: ROUTES.mobileIntro,
    element: <MobileIntro />,
  },
  {
    path: ROUTES.appLogin,
    element: <AppLogin />,
  },
  {
    path: ROUTES.requestAppAccess,
    element: <RequestAppAccess />,
  },
  {
    path: ROUTES.waitlist,
    element: <Waitlist />,
  },
  {
    path: ROUTES.pdf_generation,
    element: <PDFGeneration />,
  },
  {
    path: ROUTES.qrDownload,
    element: <QRDownload />,
  },
  {
    path: ROUTES.dataDeletionRequest,
    element: <DataDeletionRequest />,
  },
  { path: ROUTES.login, element: <Login /> },
  { path: ROUTES.signUp, element: <SignUp /> },
  { path: ROUTES.signOut, element: <Signout /> },
  { path: ROUTES.forgotPassword, element: <ForgotPassword /> },
  { path: ROUTES.resetPassword, element: <ResetPassword /> },
  { path: ROUTES.validationAccount, element: <ValidationAccount /> },
  {
    path: ROUTES.jobCreator,
    element: <JobCreator />,
  },
  {
    path: ROUTES.documentSigning,
    element: <DocumentSigning />,
  },
];

const tenantAdminRoutes: RouteObject[] = [
  // ... existing code ...
];

const routes: RouteObject[] = [
  {
    path: Config.BASEPATH,
    children: [
      // Document signing route is independent of Root/admin session
      // so the webview session doesn't expire as long as the external API token is valid
      {
        path: ROUTES.documentSigning,
        element: <DocumentSigning />,
      },
      {
        element: <Root />,
        children: [
          {
            element: <Authenticated />,
            children: [{ element: <Agency />, children: [...privateRoutes] }],
          },
          { children: [...publicRoutes] },
        ],
      },
    ],
  },
];

const AppRouter = () => {
  const routing = useRoutes(routes);

  return routing;
};

export default AppRouter;
